import React from 'react';

import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Drawer,
  Autocomplete,
} from '@mui/material';

import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SummarizeIcon from '@mui/icons-material/Summarize';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Client from './components/Client';
import Config from './components/ClientConfig';


const OPERATION_LABELS = {
  1: 'Fiscal printer disabled',
  2: 'Fiscal printer enabled',
  3: 'Cash refund processed',
  4: 'Receipt reprint requested',
  5: 'Fiscal receipt force closed',
  6: 'Order cancelled',
  7: 'Cart cleared',
  8: 'Cart item removed',
};


function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function dateDaysAgoISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function formatDateTime(value) {
  if (!value) return '-';

  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;

    return d.toLocaleString('ro-RO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return value;
  }
}

function getOperationLabel(value, operations = []) {
  const found = operations.find((op) => String(op.value) === String(value));
  if (found) return found.label;

  return OPERATION_LABELS[value] || `Operation ${value}`;
}

function getOperationSeverity(operation) {
  const op = Number(operation);
  if ([1, 5].includes(op)) return 'high';     // Fiscal printer disabled, forced close
  if ([3, 6].includes(op)) return 'medium';   // Refund, cancelled order
  if ([4, 7, 8].includes(op)) return 'low';   // Reprint/cart actions

  return 'normal';
}

function getOperationSeverityLabel(operation) {
  const severity = getOperationSeverity(operation);
  if (severity === 'high') return 'High attention';
  if (severity === 'medium') return 'Medium attention';
  if (severity === 'low') return 'Low attention';

  return 'Normal';
}

function getOperationChipColor(operation) {
  const severity = getOperationSeverity(operation);
  if (severity === 'high') return 'error';
  if (severity === 'medium') return 'warning';
  if (severity === 'low') return 'default';

  return 'success';
}

function getOperationChipVariant(operation) {
  const severity = getOperationSeverity(operation);
  if (severity === 'high' || severity === 'medium') {
    return 'filled';
  }

  return 'outlined';
}

function getPosLabel(row) {
  if (row.location_name) return row.location_name;
  if (row.pos_name) return row.pos_name;

  if (row.pos && typeof row.pos === 'object') {
    return row.pos.name || row.pos.iref || row.pos.id || '-';
  }

  return row.pos || '-';
}

function getLocationKey(row) {
  return row.location || row.location_name || row.pos || row.pos_name || 'unknown';
}

function getLocationName(row) {
  return row.location_name || row.pos_name || row.location || row.pos || 'Unknown location';
}

function getAttentionCount(location) {
  return Object.keys(location.operations || {}).reduce((total, operation) => {
    const severity = getOperationSeverity(operation);
    if (severity === 'high' || severity === 'medium') {
      return total + (location.operations[operation] || 0);
    }

    return total;
  }, 0);
}

function buildLocationFilterOptions(actions, operations) {
  return buildLocationSummary(actions || [], operations || []).map((location) => ({
    key: location.key,
    label: location.name,
  }));
}

function buildLocationSummary(actions, operations) {
  const map = {};

  actions.forEach((row) => {
    const key = getLocationKey(row);
    const name = getLocationName(row);

    if (!map[key]) {
      map[key] = {
        key,
        name,
        total: 0,
        lastAction: null,
        lastActionTs: 0,
        operations: {},
      };
    }

    map[key].total += 1;

    const operation = row.operation;
    map[key].operations[operation] = (map[key].operations[operation] || 0) + 1;

    const occurredTs = row.occurred_on
      ? new Date(row.occurred_on).getTime()
      : 0;

    if (!Number.isNaN(occurredTs) && occurredTs > (map[key].lastActionTs || 0)) {
      map[key].lastAction = row.occurred_on;
      map[key].lastActionTs = occurredTs;
    }
  });

  return Object.values(map)
    .map((location) => {
      const topOperationEntry = Object.entries(location.operations)
        .sort((a, b) => b[1] - a[1])[0];

      const topOperation = topOperationEntry ? Number(topOperationEntry[0]) : null;
      const topOperationCount = topOperationEntry ? topOperationEntry[1] : 0;

      return {
        ...location,
        attentionCount: getAttentionCount(location),
        topOperation,
        topOperationCount,
        topOperationLabel: topOperation
          ? getOperationLabel(topOperation, operations)
          : '-',
      };
    })
    .sort((a, b) => {
      // 1. Attention level first
      if (b.attentionCount !== a.attentionCount) {
        return b.attentionCount - a.attentionCount;
      }
      // 2. Location name alphabetically
      const nameCompare = String(a.name || '').localeCompare(
        String(b.name || ''),
        'ro',
        { sensitivity: 'base' }
      );
      if (nameCompare !== 0) {
        return nameCompare;
      }
      // 3. Last action/date descending
      return (b.lastActionTs || 0) - (a.lastActionTs || 0);
    });
}

function csvEscape(value) {
  if (value === null || value === undefined) return '';

  const text = String(value);

  if (
    text.includes('"') ||
    text.includes(',') ||
    text.includes('\n') ||
    text.includes('\r')
  ) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

function downloadCsv(filename, rows) {
  const csvContent = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function safeFilename(value) {
  return String(value || 'logs')
    .trim()
    .replace(/[^\w\-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 80);
}


class OperatorAuditLogs extends React.Component {
  constructor(props) {
    super(props);

    this.client = new Client();
    this.client.prefix = Config.API_URL_LOGS_POS;

    this.state = {
      loading: false,
      error: null,

      dateFrom: dateDaysAgoISO(1),
      dateTo: todayISO(),
      operation: '',
      limit: 500,

      operations: [],
      summary: {
        actions_total: 0,
        counter_total: 0,
        by_operation: [],
        by_pos: [],
        by_employee: [],
      },
      actions: [],
      counters: [],

      selectedLocationKey: null,
      selectedLocationName: '',
      detailsDrawerOpen: false,
      attentionOnly: false,
      filtersOpen: false,
      locationFilter: null,
      expanded: {},
    };
  }

  componentDidMount() {
    this.loadReport();
  }

  buildQueryData = () => {
    const {
      dateFrom,
      dateTo,
      operation,
      locationFilter,
      limit,
    } = this.state;

    const queryData = {
      date_from: dateFrom,
      date_to: dateTo,
      limit,
    };

    if (operation !== '') {
      queryData.operation = operation;
    }

    if (locationFilter && locationFilter.key) {
      queryData.location = locationFilter.key;
    }

    return queryData;
  };

  loadReport = () => {
    this.setState({
      loading: true,
      error: null,
    });

    try {
      const result = this.client.getData('report/', this.buildQueryData());

      if (!result) {
        this.setState({
          loading: false,
          error: 'No response received from log report endpoint.',
        });
        return;
      }

      this.setState({
        loading: false,
        operations: result.operations || [],
        summary: result.summary || {
          actions_total: 0,
          counter_total: 0,
          by_operation: [],
          by_pos: [],
          by_employee: [],
        },
        actions: result.actions || [],
        counters: result.counters || [],
        selectedLocationKey: null,
        selectedLocationName: '',
        detailsDrawerOpen: false,
        expanded: {},
      });
    } catch (e) {
      this.setState({
        loading: false,
        error: 'Could not load operator logs.',
      });
    }
  };

  resetFilters = () => {
    this.setState(
      {
        dateFrom: dateDaysAgoISO(1),
        dateTo: todayISO(),
        operation: '',
        limit: 500,
        selectedLocationKey: null,
        selectedLocationName: '',
        detailsDrawerOpen: false,
        expanded: {},
        locationFilter: null,
      },
      this.loadReport
    );
  };

  toggleExpanded = (id) => {
    this.setState((prevState) => ({
      expanded: {
        ...prevState.expanded,
        [id]: !prevState.expanded[id],
      },
    }));
  };

  selectLocation = (location) => {
    this.setState({
      selectedLocationKey: location.key,
      selectedLocationName: location.name,
      detailsDrawerOpen: true,
      expanded: {},
    });
  };

  closeDetailsDrawer = () => {
    this.setState({
      detailsDrawerOpen: false,
      expanded: {},
    });
  };

  renderSummaryCard(title, value, subtitle, icon) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 0.75, sm: 1 },
          borderRadius: 2,
          height: '100%',
          bgcolor: '#fff',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: { xs: 30, sm: 32 },
              height: { xs: 30, sm: 32 },
              borderRadius: 2,
              bgcolor: 'rgba(15, 23, 42, 0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {title}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                lineHeight: 1.15,
                fontWeight: 700,
                fontSize: { xs: 16, sm: 20 },
              }}
            >
              {value}
            </Typography>

            {subtitle ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 260,
                }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Paper>
    );
  }

  renderFilterFields(operationOptions = [], locationOptions = []) {
    const {
      dateFrom,
      dateTo,
      operation,
      locationFilter,
      limit,
      loading,
    } = this.state;

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            md: '1fr 1fr 1.4fr 1.8fr 0.8fr auto auto',
          },
          gap: 1,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          type="date"
          label="From"
          value={dateFrom}
          onChange={(e) => this.setState({ dateFrom: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          size="small"
          type="date"
          label="To"
          value={dateTo}
          onChange={(e) => this.setState({ dateTo: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          select
          size="small"
          label="Operation"
          value={operation}
          onChange={(e) => this.setState({ operation: e.target.value })}
          sx={{
            gridColumn: { xs: 'span 2', sm: 'auto' },
          }}
        >
          <MenuItem value="">All operations</MenuItem>
          {operationOptions.map((op) => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </TextField>

        <Autocomplete
          size="small"
          options={locationOptions || []}
          value={locationFilter}
          onChange={(event, newValue) => {
            this.setState({
              locationFilter: newValue,
              selectedLocationKey: null,
              selectedLocationName: '',
              detailsDrawerOpen: false,
              expanded: {},
            });
          }}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Location"
              placeholder="All locations"
            />
          )}
          sx={{
            gridColumn: { xs: 'span 2', sm: 'auto' },
          }}
        />

        <TextField
          size="small"
          type="number"
          label="Limit"
          value={limit}
          onChange={(e) => this.setState({ limit: e.target.value })}
          InputProps={{
            inputProps: {
              min: 1,
              max: 1000,
            },
          }}
        />

        <Button
          variant="contained"
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={this.loadReport}
          disabled={loading}
          sx={{
            minHeight: 40,
            gridColumn: { xs: 'span 1', sm: 'auto' },
          }}
        >
          Load
        </Button>

        <Tooltip title="Reset filters">
          <IconButton
            size="small"
            onClick={this.resetFilters}
            sx={{
              border: '1px solid rgba(15, 23, 42, 0.12)',
              borderRadius: 1.5,
              height: 40,
              width: 40,
              justifySelf: { xs: 'end', sm: 'auto' },
            }}
          >
            <FilterAltOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  renderFilters() {
    const {
      dateFrom,
      dateTo,
      operation,
      limit,
      operations,
      locationFilter,
      actions,
      loading,
    } = this.state;

    const locationOptions = buildLocationFilterOptions(actions, operations);

    const operationOptions = operations.length
      ? operations
      : Object.keys(OPERATION_LABELS).map((key) => ({
        value: key,
        label: OPERATION_LABELS[key],
      }));

    return (
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 0.75, sm: 1.25 },
          borderRadius: 2,
          bgcolor: '#fff',
          flexShrink: 0,
        }}
      >
        <Stack spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <TuneIcon fontSize="small" />

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Filters
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {dateFrom} → {dateTo}
                  {operation !== '' ? ' · operation selected' : ''}
                  {locationFilter ? ' · location selected' : ''}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center">
              <Button
                size="small"
                variant={this.state.attentionOnly ? 'contained' : 'outlined'}
                color="warning"
                startIcon={<WarningAmberIcon fontSize="small" />}
                onClick={() => this.setState({ attentionOnly: !this.state.attentionOnly })}
                sx={{
                  minHeight: 34,
                  whiteSpace: 'nowrap',
                }}
              >
                Attention
              </Button>

              <Button
                size="small"
                variant="outlined"
                onClick={() => this.setState({ filtersOpen: !this.state.filtersOpen })}
                sx={{
                  minHeight: 34,
                  display: { xs: 'inline-flex', sm: 'none' },
                }}
              >
                {this.state.filtersOpen ? 'Hide' : 'Show'}
              </Button>
            </Stack>
          </Stack>

          <Collapse
            in={this.state.filtersOpen}
            timeout="auto"
            unmountOnExit={false}
            sx={{
              display: { xs: 'block', sm: 'none' },
            }}
          >
            {this.renderFilterFields(operationOptions, locationOptions)}
          </Collapse>

          <Box
            sx={{
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {this.renderFilterFields(operationOptions, locationOptions)}
          </Box>
        </Stack>
      </Paper>
    );
  }

  exportSelectedLocationLogs = () => {
    const {
      actions,
      selectedLocationKey,
      selectedLocationName,
      dateFrom,
      dateTo,
    } = this.state;

    if (!selectedLocationKey) {
      return;
    }

    const visibleActions = actions.filter(
      (row) => getLocationKey(row) === selectedLocationKey
    );

    const rows = [
      [
        'Occurred on',
        'Received on backend',
        'Location',
        'Location ID',
        'POS',
        'POS ID',
        'Employee',
        'Employee ID',
        'Operation',
        'Operation code',
        'Detail',
      ],
      ...visibleActions.map((row) => [
        formatDateTime(row.occurred_on),
        formatDateTime(row.receivedOn),
        row.location_name || '',
        row.location || '',
        row.pos_name || '',
        row.pos || '',
        row.employee_name || row.employee_id || 'unknown',
        row.employee_id || '',
        row.operation_label || getOperationLabel(row.operation, this.state.operations),
        row.operation || '',
        row.detail || '',
      ]),
    ];

    const filename = [
      'operator_logs',
      safeFilename(selectedLocationName || selectedLocationKey),
      dateFrom || 'from',
      dateTo || 'to',
    ].join('_');

    downloadCsv(`${filename}.csv`, rows);
  };

  renderSummary() {
    const {
      summary,
      operations,
      actions,
    } = this.state;

    const locations = buildLocationSummary(actions, operations);
    const byOperation = summary.by_operation || [];
    const topOperation = byOperation.length ? byOperation[0] : null;

    const topOperationLabel = topOperation
      ? `${getOperationLabel(topOperation.operation, operations)} · ${topOperation.total}`
      : 'No operation activity';

    const attentionLocations = locations.filter((location) => location.attentionCount > 0).length;

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 1,
        }}
      >
        {this.renderSummaryCard(
          'Detailed actions',
          summary.actions_total || 0,
          'Individual operator actions',
          <ReceiptLongIcon fontSize="small" />
        )}

        {this.renderSummaryCard(
          'Counter total',
          summary.counter_total || 0,
          'Aggregated daily count',
          <SummarizeIcon fontSize="small" />
        )}

        {this.renderSummaryCard(
          'Locations',
          locations.length,
          attentionLocations ? `${attentionLocations} with high-attention actions` : 'No high-attention locations',
          <StorefrontIcon fontSize="small" />
        )}

        {this.renderSummaryCard(
          'Most frequent',
          topOperation ? topOperation.total : 0,
          topOperationLabel,
          <WarningAmberIcon fontSize="small" />
        )}
      </Box>
    );
  }

  renderOperationChips() {
    const { summary, operations } = this.state;
    const byOperation = summary.by_operation || [];

    if (!byOperation.length) {
      return null;
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          px: 1.25,
          py: 0.75,
          borderRadius: 2,
          bgcolor: '#fff',
          display: { xs: 'none', md: 'block' },
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flexShrink: 0 }}>
            Activity by operation
          </Typography>

          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            sx={{
              rowGap: 0.75,
              minWidth: 0,
            }}
          >
            {byOperation.map((row) => (
              <Chip
                key={row.operation}
                size="small"
                label={`${getOperationLabel(row.operation, operations)}: ${row.total}`}
                variant="outlined"
              />
            ))}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  renderLocationSummary() {
    const {
      actions,
      operations,
      selectedLocationKey,
    } = this.state;

    let locations = buildLocationSummary(actions, operations);

    if (this.state.attentionOnly) {
      locations = locations.filter((location) => location.attentionCount > 0);
    }

    if (!locations.length) {
      return (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: '#fff',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No locations found for the selected filters.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box sx={{ px: 1.5, py: 0.75 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Summary by location
              </Typography>

              <Tooltip title="Locations are ordered by attention level. Click one location to inspect logs.">
                <IconButton
                  size="small"
                  aria-label="How locations are ordered and opened"
                  sx={{ p: 0.25, color: 'text.secondary' }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Stack>

            <Chip
              size="small"
              variant="outlined"
              label={`${locations.length} locations`}
            />
          </Stack>
        </Box>

        <Divider />

        <Box
          sx={{
            display: { xs: 'block', sm: 'none' },
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            p: 1,
          }}
        >
          <Stack spacing={1}>
            {locations.map((location) => {
              const isSelected = selectedLocationKey === location.key;

              return (
                <Paper
                  key={location.key}
                  variant="outlined"
                  onClick={() => this.selectLocation(location)}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : '#fff',
                    borderColor: location.attentionCount > 0
                      ? 'rgba(237, 108, 2, 0.45)'
                      : 'rgba(15, 23, 42, 0.12)',
                  }}
                >
                  <Stack spacing={0.75}>
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                          {location.name}
                        </Typography>

                        <Typography variant="caption" color="text.secondary" noWrap>
                          {location.key}
                        </Typography>
                      </Box>

                      <Button
                        size="small"
                        variant={isSelected ? 'contained' : 'outlined'}
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          this.selectLocation(location);
                        }}
                        sx={{ flexShrink: 0 }}
                      >
                        View
                      </Button>
                    </Stack>

                    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ rowGap: 0.75 }}>
                      <Chip size="small" variant="outlined" label={`${location.total} actions`} />

                      <Chip
                        size="small"
                        label={`${location.attentionCount} review`}
                        color={location.attentionCount > 0 ? 'warning' : 'default'}
                        variant={location.attentionCount > 0 ? 'filled' : 'outlined'}
                      />

                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${location.topOperationLabel}: ${location.topOperationCount}`}
                      />
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                      Last action: {formatDateTime(location.lastAction)}
                    </Typography>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Box>

        <TableContainer
          sx={{
            display: { xs: 'none', sm: 'block' },
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead sx={{ '& .MuiTableCell-head': { fontWeight: 700 } }}>
              <TableRow>
                <TableCell>Location</TableCell>
                <TableCell align="right">Actions</TableCell>
                <TableCell align="right">Needs review</TableCell>
                <TableCell>Most frequent action</TableCell>
                <TableCell>Last action</TableCell>
                <TableCell align="right">Details</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {locations.map((location) => {
                const isSelected = selectedLocationKey === location.key;

                return (
                  <TableRow
                    key={location.key}
                    hover
                    selected={isSelected}
                    onClick={() => this.selectLocation(location)}
                    sx={{
                      cursor: 'pointer',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(25, 118, 210, 0.08)',
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: 'rgba(25, 118, 210, 0.12)',
                      },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {location.name}
                        </Typography>

                        {location.attentionCount > 0 ? (
                          <Chip
                            size="small"
                            color="warning"
                            label="Needs review"
                            sx={{ height: 20, flexShrink: 0 }}
                          />
                        ) : null}
                      </Stack>

                      <Typography variant="caption" color="text.secondary">
                        {location.key}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={location.total}
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={location.attentionCount}
                        color={location.attentionCount > 0 ? 'warning' : 'default'}
                        variant={location.attentionCount > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${location.topOperationLabel}: ${location.topOperationCount}`}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(location.lastAction)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Button
                        size="small"
                        variant={isSelected ? 'contained' : 'outlined'}
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          this.selectLocation(location);
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  renderActionsTable() {
    const {
      actions,
      expanded,
      operations,
      selectedLocationKey,
      selectedLocationName,
    } = this.state;

    const visibleActions = selectedLocationKey
      ? actions.filter((row) => getLocationKey(row) === selectedLocationKey)
      : [];

    if (!selectedLocationKey) {
      return (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: '#fff',
            height: '100%',
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Box sx={{ maxWidth: 360 }}>
            <StorefrontIcon sx={{ fontSize: 42, opacity: 0.35, mb: 1 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Select a location
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Start from the location summary on the left. Detailed logs will appear here only for the selected location.
            </Typography>
          </Box>
        </Paper>
      );
    }

    if (!visibleActions.length) {
      return (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#fff',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            p: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No logs found for the selected location.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ px: 1.5, py: 0.75, flexShrink: 0 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle2"
                title={`Logs for ${selectedLocationName}`}
                noWrap
                sx={{ fontWeight: 700, minWidth: 0 }}
              >
                Logs for {selectedLocationName}
              </Typography>

              <Tooltip title="Details are hidden by default. Expand only the rows you need.">
                <IconButton
                  size="small"
                  aria-label="How to inspect action details"
                  sx={{ p: 0.25, color: 'text.secondary', flexShrink: 0 }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Chip
                size="small"
                variant="outlined"
                label={`${visibleActions.length} rows`}
              />

              <Button
                size="small"
                variant="outlined"
                onClick={this.closeDetailsDrawer}
              >
                Close
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Divider />

        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead sx={{ '& .MuiTableCell-head': { fontWeight: 700 } }}>
              <TableRow>
                <TableCell sx={{ width: 42 }} />
                <TableCell>Time</TableCell>
                <TableCell>Operation</TableCell>
                <TableCell>POS</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell align="right">Detail</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleActions.map((row, index) => {
                const rowId = row.id || `${row.occurred_on}-${index}`;
                const isOpen = !!expanded[rowId];
                const hasDetail = !!row.detail;

                return (
                  <React.Fragment key={rowId}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => this.toggleExpanded(rowId)}
                          disabled={!hasDetail}
                        >
                          {isOpen ? (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {formatDateTime(row.occurred_on)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Tooltip title={getOperationSeverityLabel(row.operation)}>
                          <Chip
                            size="small"
                            label={row.operation_label || getOperationLabel(row.operation, operations)}
                            color={getOperationChipColor(row.operation)}
                            variant={getOperationChipVariant(row.operation)}
                          />
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                          {getPosLabel(row)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Tooltip title={row.employee_id || ''}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 190 }}>
                            {row.employee_name || row.employee_id || 'unknown'}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="right">
                        {hasDetail ? (
                          <Chip
                            size="small"
                            label={isOpen ? 'Hide' : 'View'}
                            variant="outlined"
                            onClick={() => this.toggleExpanded(rowId)}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          p: 0,
                          borderBottom: isOpen ? '1px solid rgba(224, 224, 224, 1)' : 0,
                        }}
                      >
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              px: 2,
                              py: 1.5,
                              bgcolor: 'rgba(15, 23, 42, 0.025)',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  md: 'repeat(4, minmax(0, 1fr))',
                                },
                                gap: 1.25,
                              }}
                            >
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  POS
                                </Typography>
                                <Typography variant="body2">
                                  {row.pos_name || row.pos || '-'}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Location
                                </Typography>
                                <Typography variant="body2">
                                  {row.location_name || row.location || '-'}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Received on backend
                                </Typography>
                                <Typography variant="body2">
                                  {formatDateTime(row.receivedOn)}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Operation code
                                </Typography>
                                <Typography variant="body2">
                                  {row.operation}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ mt: 1.25 }}>
                              <Typography variant="caption" color="text.secondary">
                                Detail
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.25,
                                  p: 1,
                                  borderRadius: 1.5,
                                  bgcolor: '#fff',
                                  border: '1px solid rgba(15, 23, 42, 0.08)',
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {row.detail || 'No detail available.'}
                              </Typography>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  renderDetailsDrawer() {
    const {
      detailsDrawerOpen,
      selectedLocationName,
      selectedLocationKey,
      actions,
    } = this.state;

    const visibleActions = selectedLocationKey
      ? actions.filter((row) => getLocationKey(row) === selectedLocationKey)
      : [];

    return (
      <Drawer
        anchor="right"
        open={detailsDrawerOpen}
        onClose={this.closeDetailsDrawer}
        PaperProps={{
          sx: {
            width: {
              xs: '100%',
              sm: 720,
              md: 920,
              lg: 1040,
            },
            maxWidth: '100%',
            bgcolor: 'background.default',
          },
        }}
      >
        <Box
          sx={{
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            p: { xs: 1, sm: 1.5 },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              bgcolor: '#fff',
              mb: 1,
              px: 1.5,
              py: 1.25,
              flexShrink: 0,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
                  {selectedLocationName || 'Location logs'}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {visibleActions.length} detailed log rows
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.75} alignItems="center">
                <Tooltip title="Export location logs">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon fontSize="small" />}
                      onClick={this.exportSelectedLocationLogs}
                      disabled={!visibleActions.length}
                      sx={{
                        minHeight: 34,
                        display: { xs: 'none', sm: 'inline-flex' },
                      }}
                    >
                      Export
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title="Export location logs">
                  <span>
                    <IconButton
                      size="small"
                      onClick={this.exportSelectedLocationLogs}
                      disabled={!visibleActions.length}
                      sx={{
                        display: { xs: 'inline-flex', sm: 'none' },
                        border: '1px solid rgba(15, 23, 42, 0.12)',
                        borderRadius: 1.5,
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Close details">
                  <IconButton onClick={this.closeDetailsDrawer}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
            }}
          >
            {this.renderActionsTable()}
          </Box>
        </Box>
      </Drawer>
    );
  }

  render() {
    const { loading, error } = this.state;

    return (
      <Box
        sx={{
          p: { xs: 1, sm: 1.5, md: 2 },
          bgcolor: 'background.default',
          height: {
            xs: 'calc(100dvh - 56px)',
            md: 'calc(100dvh - 64px)',
          },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          spacing={{ xs: 0.75, sm: 1.25 }}
          sx={{
            height: '100%',
            minHeight: 0,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Operator audit logs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start from locations, then open detailed POS actions only when needed.
            </Typography>
          </Box>

          {this.renderFilters()}

          {error ? (
            <Alert severity="error">
              {error}
            </Alert>
          ) : null}

          {loading ? (
            <Alert severity="info" icon={<CircularProgress size={18} />}>
              Loading operator logs...
            </Alert>
          ) : null}

          {this.renderSummary()}

          {this.renderOperationChips()}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
            }}
          >
            {this.renderLocationSummary()}
          </Box>

          {this.renderDetailsDrawer()}
        </Stack>
      </Box>
    );
  }
}

export default OperatorAuditLogs;
